import { buildCollection, buildProperty, EntityCustomView } from "@firecms/core";
import { UserActionsView } from "../components/UserActionsView";
import { languagesSubcollection } from "./languages";
import { HomeButton } from "../components/HomeButton";

// Proficiency levels matching backend (include Pre-A1)
const proficiencyLevels = ["Pre-A1", "A1", "A2", "B1", "B2", "C1", "C2"] as const;

export type User = {
    user_id: number;
    username?: string;
    full_name?: string;
    language: string;
    active_language?: string;
    proficiency_level?: string;
    bio?: string;
    is_registered: boolean;
    has_completed_first_assessment: boolean;
    subscription_tier: string;
    subscription_expires_at?: Date;
    bonus_text_credits: number;
    bonus_voice_credits: number;
    weekly_pass_expires_at?: Date;
    weekly_pass_voice_remaining: number;
    // Flattened preferences
    pref_daily_goal: number;
    pref_preferred_topics: string[];
    pref_notifications_enabled: boolean;
    pref_voice_enabled: boolean;
    // Flattened stats
    stats_streak: number;
    stats_longest_streak: number;
    stats_total_sessions: number;
    stats_sessions_today: number;
    stats_text_messages_today: number;
    stats_voice_messages_today: number;
    stats_last_session_date?: Date;
    // Gamification
    xp: number;
    xp_level: number;
    badge_count: number;
    // Token tracking
    token_usage: number;
    monthly_token_usage: number;
    last_token_reset?: Date;
    // Additional stats fields
    stats_last_streak_update?: Date;
    stats_last_notification_type?: string;
    // Consent & caching
    consent_version?: string;
    cached_weak_areas?: string[];
    weak_areas_updated_at?: Date;
    // Timestamps
    first_seen: Date;
    last_seen: Date;
    last_notified_at?: Date;
    // Status
    is_blocked: boolean;
    consent_date?: Date;
    // Referral
    referred_by?: string;
};

const userViews: EntityCustomView<User>[] = [
    {
        key: "actions",
        name: "Actions",
        Builder: UserActionsView,
    },
];

export const usersCollection = buildCollection<User>({
    id: "users",
    name: "Users",
    singularName: "User",
    path: "users",
    icon: "People",
    group: "User Management",
    description: "Manage user profiles, subscriptions, and quotas",
    textSearchEnabled: true,
    defaultSize: "m",
    entityViews: userViews,
    Actions: HomeButton,
    callbacks: {
        onSaveFailure: (props) => {
            console.error("Failed to save user:", props);
            // The error will be shown by FireCMS automatically
        },
    },
    permissions: {
        edit: true,
        create: false,
        delete: false,
    },
    properties: {
        user_id: buildProperty({
            name: "Telegram User ID",
            dataType: "number",
            readOnly: true,
        }),
        username: buildProperty({
            name: "Username",
            dataType: "string",
            readOnly: true,
        }),
        full_name: buildProperty({
            name: "Full Name",
            dataType: "string",
            readOnly: true,
        }),
        language: buildProperty({
            name: "UI Language",
            dataType: "string",
            enumValues: {
                en: "English",
                fa: "فارسی",
                nl: "Nederlands",
            },
            validation: { required: false },
        }),
        active_language: buildProperty({
            name: "Active Language",
            dataType: "string",
            description: "Currently selected learning language code",
            readOnly: true,
        }),
        proficiency_level: buildProperty({
            name: "Proficiency Level",
            dataType: "string",
            description: "Cached level for the active language",
            enumValues: Object.fromEntries(proficiencyLevels.map((l) => [l, l])),
            validation: { required: false },
        }),
        subscription_tier: buildProperty({
            name: "Subscription Tier",
            dataType: "string",
            enumValues: {
                free: "🆓 Free",
                pro: "⭐ Pro",
            },
            columnWidth: 140,
            readOnly: true,
            validation: { required: true },
        }),
        subscription_expires_at: buildProperty({
            name: "Subscription Expires",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
            validation: { required: false },
        }),
        bonus_text_credits: buildProperty({
            name: "Bonus Text Credits",
            dataType: "number",
            description: "Non-expiring text message credits from booster packs",
            readOnly: true,
            validation: { required: false },
        }),
        bonus_voice_credits: buildProperty({
            name: "Bonus Voice Credits",
            dataType: "number",
            description: "Non-expiring voice message credits from booster packs",
            readOnly: true,
            validation: { required: false },
        }),
        weekly_pass_expires_at: buildProperty({
            name: "Weekly Pass Expires",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
            validation: { required: false },
        }),
        weekly_pass_voice_remaining: buildProperty({
            name: "Weekly Pass Voice Remaining",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        pref_daily_goal: buildProperty({
            name: "Daily Goal",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        pref_preferred_topics: buildProperty({
            name: "Preferred Topics",
            dataType: "array",
            of: { dataType: "string" },
            readOnly: true,
        }),
        pref_notifications_enabled: buildProperty({
            name: "Notifications Enabled",
            dataType: "boolean",
            readOnly: true,
        }),
        pref_voice_enabled: buildProperty({
            name: "Voice Enabled",
            dataType: "boolean",
            readOnly: true,
        }),
        xp: buildProperty({
            name: "XP",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        xp_level: buildProperty({
            name: "Level",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        badge_count: buildProperty({
            name: "Badges Earned",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        stats_streak: buildProperty({
            name: "Current Streak",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        stats_longest_streak: buildProperty({
            name: "Longest Streak",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        stats_total_sessions: buildProperty({
            name: "Total Sessions",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        stats_sessions_today: buildProperty({
            name: "Sessions Today",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        stats_text_messages_today: buildProperty({
            name: "Text Messages Today",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        stats_voice_messages_today: buildProperty({
            name: "Voice Messages Today",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        stats_last_session_date: buildProperty({
            name: "Last Session Date",
            dataType: "date",
            mode: "date",
            readOnly: true,
        }),
        token_usage: buildProperty({
            name: "Lifetime Token Usage",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        monthly_token_usage: buildProperty({
            name: "Monthly Token Usage",
            dataType: "number",
            readOnly: true,
            validation: { required: false },
        }),
        last_token_reset: buildProperty({
            name: "Last Token Reset",
            dataType: "date",
            readOnly: true,
            validation: { required: false },
        }),
        stats_last_streak_update: buildProperty({
            name: "Last Streak Update",
            dataType: "date",
            readOnly: true,
            validation: { required: false },
        }),
        stats_last_notification_type: buildProperty({
            name: "Last Notification Type",
            dataType: "string",
            readOnly: true,
            validation: { required: false },
        }),
        consent_version: buildProperty({
            name: "Consent Version",
            dataType: "string",
            readOnly: true,
            validation: { required: false },
        }),
        cached_weak_areas: buildProperty({
            name: "Cached Weak Areas",
            dataType: "array",
            of: { dataType: "string" },
            readOnly: true,
            validation: { required: false },
        }),
        weak_areas_updated_at: buildProperty({
            name: "Weak Areas Updated",
            dataType: "date",
            readOnly: true,
            validation: { required: false },
        }),
        first_seen: buildProperty({
            name: "First Seen",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        last_seen: buildProperty({
            name: "Last Seen",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        last_notified_at: buildProperty({
            name: "Last Notified",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        is_registered: buildProperty({
            name: "Registered",
            dataType: "boolean",
            readOnly: true,
        }),
        has_completed_first_assessment: buildProperty({
            name: "First Assessment Done",
            dataType: "boolean",
            description: "Whether user has completed their first (free) assessment",
            readOnly: true,
        }),
        is_blocked: buildProperty({
            name: "Blocked Bot",
            dataType: "boolean",
            readOnly: true,
        }),
        consent_date: buildProperty({
            name: "Consent Date",
            dataType: "date",
            readOnly: true,
        }),
        bio: buildProperty({
            name: "Bio",
            dataType: "string",
            readOnly: true,
        }),
        referred_by: buildProperty({
            name: "Referred By",
            dataType: "string",
            description: "User ID of the referrer",
            readOnly: true,
        }),
    },
    propertiesOrder: [
        "user_id",
        "full_name",
        "subscription_tier",
        "xp_level",
        "xp",
        "badge_count",
        "stats_streak",
        "last_seen",
    ],
    subcollections: [languagesSubcollection],
});
